import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API_BASE, timeout: 45000 });

const ADMIN_TOKEN_KEY = "ttite_admin_token";

/** Log a swallowed problem without ever blocking the customer. */
export function warnQuietly(where, error) {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[t-tite] ${where}:`, error);
  }
}

/**
 * The admin token lives in sessionStorage, not localStorage, so it is dropped
 * the moment the tab closes. That keeps a signed-in session off a shared or
 * borrowed machine. Any token left in localStorage by an older build is cleared
 * on first read.
 */
export const adminToken = {
  get: () => {
    try {
      const legacy = window.localStorage.getItem(ADMIN_TOKEN_KEY);
      if (legacy) {
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
        window.sessionStorage.setItem(ADMIN_TOKEN_KEY, legacy);
        return legacy;
      }
      return window.sessionStorage.getItem(ADMIN_TOKEN_KEY);
    } catch (error) {
      warnQuietly("could not read the admin token", error);
      return null;
    }
  },
  set: (token) => {
    try {
      window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    } catch (error) {
      warnQuietly("could not store the admin token", error);
    }
  },
  clear: () => {
    try {
      window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
      window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    } catch (error) {
      warnQuietly("could not clear the admin token", error);
    }
  },
};

function authHeaders() {
  const token = adminToken.get();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Pull the {field: message} map out of a FastAPI 422 detail payload. */
export function fieldErrorsFrom(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.errors) return detail.errors;
  return null;
}

export function messageFrom(error, fallback = "Something went wrong on our side. Please try again.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (error?.message === "Network Error") {
    return "We couldn't reach our server. Please check your connection and try again.";
  }
  return fallback;
}

/* ------------------------------- public API ------------------------------- */

export const api = {
  health: () => http.get("/health").then((r) => r.data),

  startSession: (body) => http.post("/session/start", body).then((r) => r.data),
  getSession: (sessionId) => http.get(`/session/${sessionId}`).then((r) => r.data),
  saveSession: (sessionId, body) => http.put(`/session/${sessionId}`, body).then((r) => r.data),
  resumeLink: (sessionId, body) =>
    http.post(`/session/${sessionId}/resume-link`, body).then((r) => r.data),
  evaluate: (sessionId, body) =>
    http.post(`/session/${sessionId}/evaluate`, body).then((r) => r.data),
  submitContactGate: (sessionId, body) =>
    http.post(`/session/${sessionId}/contact`, body).then((r) => r.data),

  getQuote: (ref) => http.get(`/quote/${encodeURIComponent(ref)}`).then((r) => r.data),
  quotePdfUrl: (ref) => `${API_BASE}/quote/${encodeURIComponent(ref)}/pdf`,
  nextStep: (ref, choice) =>
    http.post(`/quote/${encodeURIComponent(ref)}/next-step`, { choice }).then((r) => r.data),

  submitFallback: (body) => http.post("/fallback", body).then((r) => r.data),
};

/* -------------------------------- admin API ------------------------------- */

export const adminApi = {
  login: (email, password) =>
    http.post("/admin/login", { email, password }).then((r) => r.data),
  me: () => http.get("/admin/me", { headers: authHeaders() }).then((r) => r.data),
  stats: () => http.get("/admin/stats", { headers: authHeaders() }).then((r) => r.data),
  sessions: (params) =>
    http.get("/admin/sessions", { params, headers: authHeaders() }).then((r) => r.data),
  leads: (params) =>
    http.get("/admin/leads", { params, headers: authHeaders() }).then((r) => r.data),
  fallbacks: (params) =>
    http.get("/admin/fallbacks", { params, headers: authHeaders() }).then((r) => r.data),

  /* ---- needs a human (escalations + contact requests) ---- */
  escalations: (params) =>
    http.get("/admin/escalations", { params, headers: authHeaders() }).then((r) => r.data),
  markEscalationHandled: (reference, handled) =>
    http
      .post(
        `/admin/escalation/${encodeURIComponent(reference)}/handled`,
        { handled },
        { headers: authHeaders() },
      )
      .then((r) => r.data),
  /* ---- outsourced finance scoping enquiries ---- */
  setFinanceEnquiryStatus: (reference, status) =>
    http
      .post(
        `/admin/finance-enquiry/${encodeURIComponent(reference)}/status`,
        { status },
        { headers: authHeaders() },
      )
      .then((r) => r.data),

  /* ---- who owns this one: the team roster and the assignments ---- */
  team: () => http.get("/admin/team", { headers: authHeaders() }).then((r) => r.data),
  addTeamMember: (name) =>
    http.post("/admin/team", { name }, { headers: authHeaders() }).then((r) => r.data),
  removeTeamMember: (memberId) =>
    http
      .delete(`/admin/team/${encodeURIComponent(memberId)}`, { headers: authHeaders() })
      .then((r) => r.data),
  /* `owner` is a roster name, or null to hand the work back to nobody */
  assignEscalationOwner: (reference, owner) =>
    http
      .post(
        `/admin/escalation/${encodeURIComponent(reference)}/owner`,
        { owner: owner || null },
        { headers: authHeaders() },
      )
      .then((r) => r.data),
  assignFinanceEnquiryOwner: (reference, owner) =>
    http
      .post(
        `/admin/finance-enquiry/${encodeURIComponent(reference)}/owner`,
        { owner: owner || null },
        { headers: authHeaders() },
      )
      .then((r) => r.data),

  sessionDetail: (sessionId) =>
    http.get(`/admin/session/${sessionId}`, { headers: authHeaders() }).then((r) => r.data),
  /* one quote's own answers and workings, found by its reference rather than
     by the session, which a repeat customer shares across several quotes */
  leadDetail: (quoteRef) =>
    http
      .get(`/admin/lead/${encodeURIComponent(quoteRef)}/detail`, { headers: authHeaders() })
      .then((r) => r.data),
  quoteDetail: (quoteRef) =>
    http
      .get(`/admin/quote/${encodeURIComponent(quoteRef)}`, { headers: authHeaders() })
      .then((r) => r.data),

  /* ---- follow-up board ---- */
  pipeline: (params) =>
    http.get("/admin/pipeline", { params, headers: authHeaders() }).then((r) => r.data),
  updateLead: (quoteRef, body) =>
    http
      .patch(`/admin/lead/${encodeURIComponent(quoteRef)}`, body, { headers: authHeaders() })
      .then((r) => r.data),
  /* every quote this customer has had, side by side */
  leadHistory: (quoteRef) =>
    http
      .get(`/admin/lead/${encodeURIComponent(quoteRef)}/history`, { headers: authHeaders() })
      .then((r) => r.data),
  resendQuoteEmail: (quoteRef) =>
    http
      .post(`/admin/lead/${encodeURIComponent(quoteRef)}/email`, {}, { headers: authHeaders() })
      .then((r) => r.data),

  /* ---- rate card ---- */
  rateCard: () => http.get("/admin/rate-card", { headers: authHeaders() }).then((r) => r.data),
  saveRateCard: (rates) =>
    http.put("/admin/rate-card", { rates }, { headers: authHeaders() }).then((r) => r.data),
  resetRateCard: () =>
    http.post("/admin/rate-card/reset", {}, { headers: authHeaders() }).then((r) => r.data),
  rateCardHistory: (params) =>
    http
      .get("/admin/rate-card/history", { params, headers: authHeaders() })
      .then((r) => r.data),
  restoreRateCard: (versionId) =>
    http
      .post(`/admin/rate-card/restore/${encodeURIComponent(versionId)}`, {}, {
        headers: authHeaders(),
      })
      .then((r) => r.data),

  /* ---- email + chase list ---- */
  emailStatus: () => http.get("/admin/email/status", { headers: authHeaders() }).then((r) => r.data),
  sendTestEmail: (to) =>
    http.post("/admin/email/test", { to }, { headers: authHeaders() }).then((r) => r.data),
  /* Asks Resend again whether our own sending domain may send. The check posts to
     Resend's sink address, so nothing reaches an inbox. */
  checkEmailDomain: () =>
    http.post("/admin/email/domain-check", {}, { headers: authHeaders() }).then((r) => r.data),
  weeklyPreview: () =>
    http.get("/admin/weekly/preview", { headers: authHeaders() }).then((r) => r.data),
  sendWeekly: (to) =>
    http.post("/admin/weekly/send", { to }, { headers: authHeaders() }).then((r) => r.data),

  digestPreview: () =>
    http.get("/admin/digest/preview", { headers: authHeaders() }).then((r) => r.data),
  sendDigest: (to) =>
    http.post("/admin/digest/send", { to }, { headers: authHeaders() }).then((r) => r.data),
  exportCsv: async (params) => {
    const response = await http.get("/admin/export", {
      params,
      headers: authHeaders(),
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `t-tite-${params?.type || "export"}.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },
};
